import { deriveOperationType } from '../../../domain/data-collection/constants';
import { cleanNumericInput, formatNumericInput } from '../../../utils/numberInput';
import { Badge, FieldLabel, SdxField, ChoiceCard, ERROR_TEXT, Hint, SectionCard } from '../UIComponents';

const BRAND = '#004A53';
const INTERACTION = '#026F6D';
const GREEN_SURFACE = '#f0f7f6';
const GREEN_BORDER = '#b7cfcc';
const MUTED = '#64748b';

function ActivitySlider({ label, value, onChange, disabled }) {
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <FieldLabel required>{label}</FieldLabel>
        <strong style={{ color: INTERACTION, minWidth: 44, textAlign: 'right' }}>{value} %</strong>
      </div>
      <input
        type="range" min={0} max={100} step={5} value={value}
        onChange={e => onChange(Number(e.target.value))}
        disabled={disabled}
        style={{ width: '100%', accentColor: INTERACTION, cursor: disabled ? 'default' : 'pointer' }}
      />
    </div>
  );
}

export default function OperationStep({ formData, setField, isReadOnly, fieldErrors }) {
  const totalAreaImported =
    Array.isArray(formData.carbonPulseImportedFields) &&
    formData.carbonPulseImportedFields.includes('totalAreaM2');
  const soldHoursImported =
    Array.isArray(formData.carbonPulseImportedFields) &&
    formData.carbonPulseImportedFields.includes('soldHoursTotal');

  const activitySum = (formData.activitySpenglerarbeiten || 0)
    + (formData.activityLackierarbeiten || 0)
    + (formData.activityWeitere || 0);
  const operationType = deriveOperationType(
    formData.activitySpenglerarbeiten,
    formData.activityLackierarbeiten,
    formData.activityWeitere
  );

  const sumOk = activitySum === 100;

  return (
    <div>
      {/* Activity */}
      <SectionCard title="Tätigkeitsschwerpunkt" badge="Pflicht">
        <ActivitySlider label="Spenglerarbeiten" value={formData.activitySpenglerarbeiten} onChange={v => setField('activitySpenglerarbeiten', v)} disabled={isReadOnly} />
        <ActivitySlider label="Lackierarbeiten" value={formData.activityLackierarbeiten} onChange={v => setField('activityLackierarbeiten', v)} disabled={isReadOnly} />
        <ActivitySlider label="Weitere" value={formData.activityWeitere} onChange={v => setField('activityWeitere', v)} disabled={isReadOnly} />

        <div style={{ padding: '10px 14px', borderRadius: 8, background: sumOk ? GREEN_SURFACE : '#fffbeb', border: `1px solid ${sumOk ? GREEN_BORDER : '#fde68a'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 600, color: sumOk ? BRAND : '#92400e' }}>
            Summe: {activitySum} %{!sumOk && ` (${activitySum > 100 ? '+' : ''}${activitySum - 100} %)`}
          </span>
          {sumOk && <span style={{ color: INTERACTION, fontSize: 18 }}>✓</span>}
        </div>
        {fieldErrors?.activity && <span style={ERROR_TEXT}>{fieldErrors.activity}</span>}

        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, color: MUTED }}>Erkannter Betriebstyp:</span>
          <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 999, background: GREEN_SURFACE, color: BRAND }}>{operationType}</span>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>(automatisch)</span>
        </div>
      </SectionCard>

      {/* Garage and building */}
      <SectionCard title="Garage & Gebäude" badge="Pflicht">
        <div style={{ marginBottom: 18 }}>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 10,
            }}
          >
            {[
              {
                value: 'independent',
                label: 'Freie Werkstatt',
                description: 'Unabhängig, keine Markenbindung',
              },
              {
                value: 'brand_bound',
                label: 'Markengebundene Werkstatt',
                description: 'An eine oder mehrere Fahrzeugmarken gebunden',
              },
            ].map(option => (
              <ChoiceCard
                key={option.value}
                label={option.label}
                description={option.description}
                selected={formData.garageType === option.value}
                onClick={() =>
                  setField('garageType', option.value)
                }
                disabled={isReadOnly}
              />
            ))}
          </div>

          {fieldErrors?.garageType && (
            <span style={ERROR_TEXT}>{fieldErrors.garageType}</span>
          )}
        </div>

        {formData.garageType === 'brand_bound' && (
          <div style={{ marginBottom: 18 }}>
            <SdxField
              label="Marken"
              value={formData.garageBrands ?? ''}
              onInput={e => setField('garageBrands', e.target.value)}
              disabled={isReadOnly || undefined}
              placeholder="z. B. BMW, MINI, Volkswagen"
              valid={fieldErrors?.garageBrands ? 'false' : undefined}
              validation-message={fieldErrors?.garageBrands || undefined}
            ></SdxField>

            <p
              style={{
                fontSize: 12,
                color: '#94a3b8',
                margin: '4px 0 0',
              }}
            >
              Mehrere Marken mit Komma trennen.
            </p>
          </div>
        )}

        <div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 10,
            }}
          >
            {[
              {
                value: 'owned',
                label: 'Eigentum',
                description: 'Gebäude gehört dem Betrieb',
              },
              {
                value: 'rented',
                label: 'Miete',
                description: 'Gebäude wird gemietet',
              },
              {
                value: 'partial',
                label: 'Teilweise',
                description: 'z.B. eigenes Hauptgebäude, gemietete Halle',
              },
            ].map(option => (
              <ChoiceCard
                key={option.value}
                label={option.label}
                description={option.description}
                selected={
                  formData.buildingRelationship === option.value
                }
                onClick={() =>
                  setField('buildingRelationship', option.value)
                }
                disabled={isReadOnly}
              />
            ))}
          </div>

          {fieldErrors?.buildingRelationship && (
            <span style={ERROR_TEXT}>
              {fieldErrors.buildingRelationship}
            </span>
          )}

          <Hint>
            Das Gebäudeverhältnis wird bei späteren Massnahmenvorschlägen
            berücksichtigt.
          </Hint>
        </div>
      </SectionCard>

      {/* Additional activities */}
      <SectionCard title="Zusatzaktivitäten" badge="optional">
        {[
          {
            field: 'hasMechanicalWorkshop',
            label: 'Mechanische Werkstatt',
          },
          {
            field: 'hasVehicleSales',
            label: 'Verkauf',
          },
          {
            field: 'hasWashingSystem',
            label: 'Eigene Waschanlage',
          },
          {
            field: 'hasFuelStation',
            label: 'Eigene Tankstelle',
          },
          {
            field: 'hasPublicChargingStations',
            label: '(Öffentliche) Ladestationen',
          },
        ].map(option => (
          <label
            key={option.field}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '6px 0',
              cursor: isReadOnly ? 'default' : 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={Boolean(formData[option.field])}
              onChange={e =>
                setField(option.field, e.target.checked)
              }
              disabled={isReadOnly}
              style={{
                width: 16,
                height: 16,
                accentColor: INTERACTION,
              }}
            />

            <span style={{ fontSize: 14, color: '#1e293b' }}>
              {option.label}
            </span>
          </label>
        ))}

        {formData.hasPublicChargingStations && (
          <div style={{ marginLeft: 26, marginTop: 8 }}>
            <SdxField
              label="Anzahl Ladepunkte"
              type="text"
              inputmode="decimal"
              value={formatNumericInput(formData.publicChargingPointsCount ?? '')}
              onInput={e =>
                setField('publicChargingPointsCount', cleanNumericInput(e.target.value))
              }
              disabled={isReadOnly || undefined}
              placeholder="z. B. 4"
              valid={fieldErrors?.publicChargingPointsCount ? 'false' : undefined}
              validation-message={fieldErrors?.publicChargingPointsCount || undefined}
            ></SdxField>
          </div>
        )}

        {formData.hasWashingSystem && (
          <Hint>
            Waschanlage erfasst — ein höherer Stromverbrauch ist plausibel
            und wird beim Vergleich berücksichtigt.
          </Hint>
        )}
      </SectionCard>

      {/* Company size */}
      <SectionCard title="Betriebsgrösse" badge="Pflicht">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <SdxField
              label="Produktive Mitarbeitende (FTE)"
              type="text"
              inputmode="decimal"
              value={formatNumericInput(formData.productiveFte ?? '')}
              onInput={e => setField('productiveFte', cleanNumericInput(e.target.value))}
              disabled={isReadOnly || undefined}
              placeholder="z. B. 8.5"
              valid={fieldErrors?.productiveFte ? 'false' : undefined}
              validation-message={fieldErrors?.productiveFte || undefined}
            ></SdxField>
            <p
              style={{
                fontSize: 12,
                color: MUTED,
                margin: '4px 0 0',
                lineHeight: 1.5,
              }}
            >
              Nur produktive Mitarbeitende in Spenglerei und Lackiererei inklusive
              Fahrzeugaufbereitung berücksichtigen. Teilzeit als Dezimalwert
              erfassen. Lehrlinge zählen als 0.5 FTE.
            </p>
          </div>
          <div>
            {totalAreaImported && (
              <Hint type="success">
                Aus der Carbonpulse-Vorab-Checkliste übernommen. Werte können hier korrigiert werden.
              </Hint>
            )}

            <div
              style={{
                marginTop: totalAreaImported ? 12 : 0,
                opacity: 1,
              }}
            >
              <SdxField
                label="Betriebsfläche gesamt (m²)"
                type="text"
                inputmode="decimal"
                value={formatNumericInput(formData.totalAreaM2 ?? '')}
                onInput={e => setField('totalAreaM2', cleanNumericInput(e.target.value))}
                disabled={isReadOnly || undefined}
                placeholder="z. B. 600"
                valid={fieldErrors?.totalAreaM2 ? 'false' : undefined}
                validation-message={fieldErrors?.totalAreaM2 || undefined}
              ></SdxField>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <SdxField
            required={false}
            label="Produktive Fläche (m²)"
            type="text"
            inputmode="decimal"
            value={formatNumericInput(formData.productiveAreaM2 ?? '')}
            onInput={e => setField('productiveAreaM2', cleanNumericInput(e.target.value))}
            disabled={isReadOnly || undefined}
            placeholder="m²"
            valid={fieldErrors?.productiveAreaM2 ? 'false' : undefined}
            validation-message={fieldErrors?.productiveAreaM2 || undefined}
          ></SdxField>
          <p
            style={{
              fontSize: 12,
              color: '#64748b',
              margin: '4px 0 0',
              lineHeight: 1.5,
            }}
          >

            Lackiererei + Spenglerei, ohne Büro/Showroom
          </p>
        </div>
      </SectionCard>

      {/* Sold hours */}
      <SectionCard title="Verkaufte Stunden" badge="Pflicht">
        {soldHoursImported && (
          <Hint type="success">
            Aus der Carbonpulse-Vorab-Checkliste übernommen. Werte können hier korrigiert werden.
          </Hint>
        )}

        <div
          style={{
            marginTop: soldHoursImported ? 12 : 0,
            opacity: 1,
          }}
        >
          <SdxField
            label="Anzahl verkaufte Stunden pro Jahr"
            type="text"
            inputmode="decimal"
            value={formatNumericInput(formData.soldHoursTotal ?? '')}
            onInput={e => setField('soldHoursTotal', cleanNumericInput(e.target.value))}
            disabled={isReadOnly || undefined}
            placeholder="z. B. 12500"
            valid={fieldErrors?.soldHoursTotal ? 'false' : undefined}
            validation-message={fieldErrors?.soldHoursTotal || undefined}
          ></SdxField>

          <Hint>
            Nur verkaufte Stunden Lackiererei &amp; Spenglerei
          </Hint>
          <Hint>
            Dies ermöglicht ein genaueres Benchmarking
          </Hint>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '12px 0 4px',
              cursor: isReadOnly ? 'default' : 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={Boolean(formData.splitSoldHours)}
              onChange={e =>
                setField('splitSoldHours', e.target.checked)
              }
              disabled={isReadOnly}
              style={{
                width: 16,
                height: 16,
                accentColor: INTERACTION,
              }}
            />

            <span style={{ fontSize: 14, color: '#1e293b' }}>
              Stunden nach Lackiererei und Spenglerei aufteilen
            </span>
            <span aria-hidden="true"><Badge type="optional" /></span>
          </label>

          {formData.splitSoldHours && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 16,
                marginTop: 8,
              }}
            >
              <SdxField
                label="Verkaufte Stunden Lackiererei"
                type="text"
                inputmode="decimal"
                value={formatNumericInput(formData.soldHoursPaint ?? '')}
                onInput={e => setField('soldHoursPaint', cleanNumericInput(e.target.value))}
                disabled={isReadOnly || undefined}
                placeholder="z. B. 7000"
                valid={fieldErrors?.soldHoursSplit ? 'false' : undefined}
              ></SdxField>

              <SdxField
                label="Verkaufte Stunden Spenglerei"
                type="text"
                inputmode="decimal"
                value={formatNumericInput(formData.soldHoursBody ?? '')}
                onInput={e => setField('soldHoursBody', cleanNumericInput(e.target.value))}
                disabled={isReadOnly || undefined}
                placeholder="z. B. 5500"
                valid={fieldErrors?.soldHoursSplit ? 'false' : undefined}
              ></SdxField>
            </div>
          )}

          {fieldErrors?.soldHoursSplit && (
            <span style={ERROR_TEXT}>
              {fieldErrors.soldHoursSplit}
            </span>
          )}
        </div>
      </SectionCard>

      {/* Revenue */}
      <SectionCard title="Jahresumsatz" badge="optional">
        <SdxField required={Boolean(formData.splitRevenue && [formData.revenuePaint, formData.revenueBody].some(value => value !== '' && value != null))}
          label="Jahresumsatz gesamt (CHF exkl. MWST)"
          type="text"
          inputmode="decimal"
          value={formatNumericInput(formData.annualRevenue ?? '')}
          onInput={e => setField('annualRevenue', cleanNumericInput(e.target.value))}
          disabled={isReadOnly || undefined}
          placeholder="z. B. 2000000"
          valid={fieldErrors?.annualRevenue ? 'false' : undefined}
          validation-message={fieldErrors?.annualRevenue || undefined}
        ></SdxField>
        <p
          style={{
            fontSize: 12,
            color: MUTED,
            margin: '6px 0 0',
            lineHeight: 1.5,
          }}
        >
          Geschäftsjahr der letzten abgeschlossenen Abrechnung verwenden.
          Nur Umsatz aus Lackiererei und Spenglerei berücksichtigen.
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0 4px', cursor: isReadOnly ? 'default' : 'pointer' }}>
          <input type="checkbox" checked={formData.splitRevenue} onChange={e => setField('splitRevenue', e.target.checked)} disabled={isReadOnly} style={{ width: 16, height: 16, accentColor: INTERACTION }} />
          <span style={{ fontSize: 14, color: '#1e293b' }}>Nach Lackiererei / Spenglerei aufteilen</span>
        </label>
        {formData.splitRevenue && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 8 }}>
            <sdx-input label="Umsatz Lackiererei (CHF)" type="text" inputmode="decimal" value={formatNumericInput(formData.revenuePaint ?? '')} onInput={e => setField('revenuePaint', cleanNumericInput(e.target.value))} disabled={isReadOnly || undefined} valid={fieldErrors?.revenuePaint ? 'false' : undefined} validation-message={fieldErrors?.revenuePaint || undefined}></sdx-input>
            <sdx-input label="Umsatz Spenglerei (CHF)" type="text" inputmode="decimal" value={formatNumericInput(formData.revenueBody ?? '')} onInput={e => setField('revenueBody', cleanNumericInput(e.target.value))} disabled={isReadOnly || undefined} valid={fieldErrors?.revenueBody ? 'false' : undefined} validation-message={fieldErrors?.revenueBody || undefined}></sdx-input>
          </div>
        )}
      </SectionCard>

      {/* Hourly rate */}
      <SectionCard title="Stundensatz" badge="optional">
        <sdx-input
          label="Ø Stundensatz (CHF/h)"
          type="text"
          inputmode="decimal"
          value={formatNumericInput(formData.averageHourlyRate ?? '')}
          onInput={e => setField('averageHourlyRate', cleanNumericInput(e.target.value))}
          disabled={isReadOnly || undefined}
          placeholder="z. B. 135"
          valid={fieldErrors?.averageHourlyRate ? 'false' : undefined}
          validation-message={fieldErrors?.averageHourlyRate || undefined}
        ></sdx-input>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0 4px', cursor: isReadOnly ? 'default' : 'pointer' }}>
          <input type="checkbox" checked={formData.splitHourlyRate} onChange={e => setField('splitHourlyRate', e.target.checked)} disabled={isReadOnly} style={{ width: 16, height: 16, accentColor: INTERACTION }} />
          <span style={{ fontSize: 14, color: '#1e293b' }}>Stundensatz nach Bereich aufschlüsseln</span>
        </label>
        {formData.splitHourlyRate && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 8 }}>
            <sdx-input label="Stundensatz Lackiererei (CHF/h)" type="text" inputmode="decimal" value={formatNumericInput(formData.hourlyRatePaint ?? '')} onInput={e => setField('hourlyRatePaint', cleanNumericInput(e.target.value))} disabled={isReadOnly || undefined} valid={fieldErrors?.hourlyRatePaint ? 'false' : undefined} validation-message={fieldErrors?.hourlyRatePaint || undefined}></sdx-input>
            <sdx-input label="Stundensatz Spenglerei (CHF/h)" type="text" inputmode="decimal" value={formatNumericInput(formData.hourlyRateBody ?? '')} onInput={e => setField('hourlyRateBody', cleanNumericInput(e.target.value))} disabled={isReadOnly || undefined} valid={fieldErrors?.hourlyRateBody ? 'false' : undefined} validation-message={fieldErrors?.hourlyRateBody || undefined}></sdx-input>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
